import React, { useState } from 'react';
import { Alert, Button, Empty, Form, Input, Pagination, Skeleton, Tag, message } from 'antd';
import { ClockCircleOutlined, ReloadOutlined, SearchOutlined, StarFilled } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import FeaturePage from '@/components/featurepage';
import PostCard from '@/components/postcard';
import { fetchMyFavorites, unfavoritePost } from '@/apis/posts';
import { syncPostMetrics } from '@/store/modules/posts';
import usePrivateList from '@/utils/usePrivateList';
import usePrivateAction from '@/utils/usePrivateAction';
import { compactQueryParams, resourceIdRule } from '@/utils/queryParams';
import './index.scss';
import { useBrowseState } from '@/utils/browseState';

const formatSavedAt = value => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const MyFavorites = () => {
  const [page, setPage] = useBrowseState('favorites:page', 1);
  const [filters, setFilters] = useBrowseState('favorites:filters', {}); const { busy, run } = usePrivateAction();
  const [removingId, setRemovingId] = useState(null);
  const [form] = Form.useForm(); const dispatch = useDispatch();
  const { data, loading, error, load, isAuthenticated } = usePrivateList(fetchMyFavorites, { ...filters, page, pageSize: 10 }, '收藏夹加载失败');
  const reload = () => { if (data?.data.length === 1 && page > 1) setPage(value => value - 1); else load().catch(() => {}); };
  const reset = () => { form.resetFields(); form.setFieldsValue({ post_id: undefined }); setFilters({}); setPage(1); if (!filters.post_id && page === 1) load().catch(() => {}); };
  const remove = async item => {
    if (busy) return;
    setRemovingId(item.id);
    try {
      await run(() => unfavoritePost(item.post_id), () => {
        dispatch(syncPostMetrics({ postId: item.post_id, is_favorited: false }));
        message.success('已取消收藏'); reload();
      }, '取消收藏失败，请重试');
    } finally { setRemovingId(null); }
  };
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="myFavorites" title="我的收藏" subtitle="保存感兴趣的帖子，方便下次查看。">
    <section className="featurePage-panel favorites-toolbar" aria-label="收藏筛选">
      <Form form={form} initialValues={filters} name="favorite-post-filters" className="favorites-filter-form" layout="inline" onFinish={values => {
        const next = compactQueryParams(values);
        if (page === 1 && JSON.stringify(next) === JSON.stringify(filters)) load().catch(() => {});
        else { setFilters(next); setPage(1); }
      }}>
        <Form.Item name="post_id" label="帖子编号" rules={[resourceIdRule('帖子编号')]}><Input placeholder="输入帖子编号" maxLength={19} inputMode="numeric" /></Form.Item>
        <div className="favorites-filter-actions">
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />} disabled={loading || busy}>筛选</Button>
          <Button onClick={reset} disabled={loading || busy}>重置</Button>
        </div>
      </Form>
      <div className="favorites-summary" aria-live="polite">
        <span>{loading ? '正在加载收藏…' : error ? '加载失败' : filters.post_id ? `筛选结果 ${data?.pagination?.total ?? data?.data.length ?? 0} 条` : `共 ${data?.pagination?.total ?? data?.data.length ?? 0} 条收藏`}</span>
        <Button icon={<ReloadOutlined />} disabled={loading || busy} onClick={() => load().catch(() => {})}>刷新</Button>
      </div>
    </section>
    {loading ? <section className="featurePage-panel"><Skeleton active /></section> : error ? <Alert type="error" showIcon title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <>
      {!data?.data.length && <section className="featurePage-panel favorites-empty"><Empty description={filters.post_id ? '没有找到对应的收藏帖子' : '暂无收藏帖子'} />{filters.post_id && <Button onClick={reset}>查看全部收藏</Button>}</section>}
      <div className="favorites-list">
        {data?.data.map(item => {
          const post = item.available ? item.post_context : null;
          const savedAt = formatSavedAt(item.created_at);
          const footer = <div className="favorites-card-footer">
              <div className="favorites-meta">
                {savedAt && <span><ClockCircleOutlined /><time dateTime={item.created_at}>收藏于 {savedAt}</time></span>}
              </div>
              <div className="favorites-card-actions">
                <Button type="text" icon={<StarFilled />} loading={busy && removingId === item.id} disabled={busy || loading} onClick={() => remove(item)}>取消收藏</Button>
              </div>
            </div>;
          return post ? <PostCard key={item.id} className="favorites-card" post={{ ...post, id: item.post_id }} footer={footer} /> :
            <article className="post-feed-card favorites-card is-unavailable" key={item.id}>
              <div className="favorites-card-heading"><span className="favorites-author"><StarFilled />收藏记录</span><Tag>暂不可用</Tag></div>
              <h2>帖子当前不可用</h2><p className="favorites-unavailable-description">可以取消收藏，或稍后再查看。</p>
              {footer}
            </article>;
        })}
      </div>
      {data?.pagination?.total > 10 && <Pagination className="favorites-pagination" current={page} pageSize={10} total={data.pagination.total} showSizeChanger={false} disabled={busy} onChange={setPage} />}
    </>}
  </FeaturePage>;
};
export default MyFavorites;
